import { fork, put, select, takeLatest } from 'redux-saga/effects'
import map from 'lodash/map'
import filter from 'lodash/filter'

import { createApiCall } from 'services/api'
import {
  ActionType,
  IAssignManager,
  IDeleteAccessToCentra,
  IDeleteAccessToClient,
  IInviteClientManager,
  IInviteTeam,
} from './model'
import { Action, ActionCallback, ActionCallbackOnly, DEFAULT_ITEM_LIST, IAxiosResponse } from '../model'
import { ActionType as CommonActionType } from '../common/model'
import { withTags } from '@centra/centra-ui'
import { AxiosError } from 'axios'

export function* getTeamSaga(): Generator {
  yield takeLatest(ActionType.GET_TEAM, function* (action: ActionCallbackOnly) {
    try {
      const { callback } = action
      const {
        auth: { user },
        team: { team },
      } = yield select()

      yield put({
        type: ActionType.SET_TEAM,
        payload: {
          ...team,
          loading: true,
        },
      })

      const isAgency = withTags({ user, tags: ['role.agency-manager', 'role.agency-primary-manager'] })
      const id = isAgency ? user.agencyId : user.clientId

      if (!id) {
        throw new Error(
          `Для пользователя не привязан id клиента или агенства! Это могло произойти из-за внутренней ошибки при регистрации. Воспользуйтесь другой учетной записью`
        )
      }

      const { status, data }: IAxiosResponse = yield createApiCall({
        url: isAgency ? `/agency/${id}/manager` : `/clients/${id}/client-managers`,
      })

      yield put({
        type: ActionType.SET_TEAM,
        payload:
          status === 200
            ? {
                ...data,
                loading: false,
              }
            : DEFAULT_ITEM_LIST,
      })

      if (callback) callback()
    } catch (error) {
      console.error(error)
    }
  })
}

export function* deleteAccessToClientSaga(): Generator {
  yield takeLatest(ActionType.DELETE_ACCESS_TO_CLIENT, function* (action: ActionCallback<IDeleteAccessToClient>) {
    try {
      const {
        payload: { managerId, clientId, clientName },
        callback,
      } = action
      const {
        auth: { user },
        team: { team },
      } = yield select()

      const { status }: IAxiosResponse = yield createApiCall({
        method: 'DELETE',
        url: clientId
          ? `/agency/${user.agencyId}/manager/${managerId}/client/${clientId}` // remove access to current client
          : `/agency/${user.agencyId}/manager/${managerId}/client`, // remove access to all clients
      })

      if (status === 200) {
        yield put({
          type: ActionType.SET_TEAM,
          payload: {
            items: map(team.items, manager => {
              // removed access to current client
              if (manager.id === managerId && clientId) {
                return {
                  ...manager,
                  clients: filter(manager.clients, managerClient => managerClient.id !== clientId),
                }
              }
              // removed access to all clients
              if (manager.id === managerId) {
                return {
                  ...manager,
                  clients: [],
                }
              }
              return {
                ...manager,
              }
            }),
            totalCount: team.totalCount,
          },
        })
        yield put({
          type: ActionType.GET_TEAM,
        })
        yield put({
          type: CommonActionType.SET_NOTIFICATION,
          payload: {
            status: 'success',
            message: clientName ? `Доступ к клиенту ${clientName} закрыт` : 'Доступ ко всем клиентам закрыт',
          },
        })
      }
      callback({ success: status === 200 })
    } catch (error) {
      // error
    }
  })
}

export function* deleteAccessToCentraSaga(): Generator {
  yield takeLatest(ActionType.DELETE_ACCESS_TO_CENTRA, function* (action: ActionCallback<IDeleteAccessToCentra>) {
    try {
      const {
        payload: { managerId, isOpen },
        callback,
      } = action
      const {
        team: { team },
      } = yield select()

      const { status }: IAxiosResponse = yield createApiCall({
        method: 'PATCH',
        url: `/clients/${managerId}/open-close`,
        data: {
          isOpen,
        },
      })

      if (status === 200) {
        yield put({
          type: ActionType.SET_TEAM,
          payload: {
            ...team,
            items: map(team.items, teamMember => {
              if (teamMember.id === managerId) {
                return {
                  ...teamMember,
                  active: isOpen,
                }
              }
              return {
                ...teamMember,
              }
            }),
          },
        })
        yield put({
          type: ActionType.GET_TEAM,
        })
        yield put({
          type: CommonActionType.SET_NOTIFICATION,
          payload: {
            status: 'success',
            message: isOpen ? 'Доступ открыт' : 'Доступ закрыт',
          },
        })
      }
      callback({ success: status === 200 })
    } catch (error) {
      // error
    }
  })
}

export function* inviteManagerSaga(): Generator {
  yield takeLatest(ActionType.INVITE_MANAGER, function* (action: ActionCallback<IInviteTeam>) {
    try {
      const { payload, callback } = action
      const {
        auth: { user },
      } = yield select()
      const response: IAxiosResponse = yield createApiCall({
        method: 'POST',
        url: `/agency/${user.agencyId}/manager/invite`,
        data: payload,
      })

      if (response.status === 200) {
        callback({ success: true })
      } else {
        const error = response as unknown as AxiosError
        callback({ success: false, fields: error?.response?.data?.fields })
      }
    } catch (error) {
      // error
    }
  })
}

export function* assignManagerSaga(): Generator {
  yield takeLatest(ActionType.ASSIGN_MANAGER, function* (action: ActionCallback<IAssignManager>) {
    try {
      const { payload, callback } = action
      const {
        auth: { user },
      } = yield select()

      const { status }: IAxiosResponse = yield createApiCall({
        method: 'POST',
        url: `/agency/${user.agencyId}/manager/${payload.managerId}/client`,
        data: {
          clientId: payload.clientId,
        },
      })

      if (status === 200) {
        yield put({
          type: ActionType.GET_TEAM,
        })
      }
      callback({ success: status === 200 })
    } catch (error) {
      // error
    }
  })
}

export function* inviteClientManagerSaga(): Generator {
  yield takeLatest(ActionType.INVITE_CLIENT_MANAGER, function* (action: ActionCallback<IInviteClientManager>) {
    try {
      const { payload, callback } = action
      const {
        auth: { clientCompany },
      } = yield select()

      const response: IAxiosResponse = yield createApiCall({
        method: 'POST',
        url: `/clients/${clientCompany.id}/managers/invite`,
        data: {
          managerEmail: payload.email,
        },
      })

      if (response.status === 200) {
        yield put({
          type: ActionType.GET_TEAM,
        })
        callback({ success: true })
      } else {
        const error = response as unknown as AxiosError
        callback({ success: false, fields: error?.response?.data?.fields })
      }
    } catch (error) {
      // error
    }
  })
}

export function* getManagerProfileSaga(): Generator {
  yield takeLatest(ActionType.GET_MANAGER_PROFILE, function* (action: Action<{ managerId: number }>) {
    try {
      const {
        payload: { managerId },
      } = action

      const { data, status }: IAxiosResponse = yield createApiCall({
        url: `/account/profile/${managerId}`,
      })

      if (status === 200) {
        yield put({
          type: ActionType.SET_MANAGER_PROFILE,
          payload: data,
        })
      } else {
        yield put({
          type: ActionType.SET_MANAGER_PROFILE,
          payload: null,
        })
      }
    } catch (error) {
      // error
    }
  })
}

export default [
  fork(inviteManagerSaga),
  fork(assignManagerSaga),
  fork(inviteClientManagerSaga),
  fork(getTeamSaga),
  fork(deleteAccessToCentraSaga),
  fork(deleteAccessToClientSaga),
  fork(getManagerProfileSaga),
]
